<template>
  <div
    v-loading="loading"
    class="container"
  >
    <BHeightAdjuster class="adjuster">
      <BLayout
        align-center
        class="header"
      >
        <TranscriptionHeader
          :zoom-phone-result="zoomPhoneResult"
          :prev-link="prevLink"
          :next-link="nextLink"
        />
      </BLayout>
      <BLayout>
        <div class="body-left">
          <SidePanel
            :zoom-phone-result-id="zoomPhoneResultId"
            :transcription-exists="transcriptionExists"
            :is-transcription-result-empty="isTranscriptionResultEmpty"
            :transcription-in-progress="transcriptionInProgress"
            :right-after-transcription="rightAfterTranscription"
            @click:play-at="playAudio"
            @update:summarized-result="handleUpdateSummarizedResult"
          />
        </div>
        <div class="body-right">
          <TranscriptionMainContent
            ref="transcriptionMainContent"
            :transcription="transcription"
            :transcription-exists="transcriptionExists"
            :is-transcription-result-empty="isTranscriptionResultEmpty"
            :transcription-in-progress="transcriptionInProgress"
            :zoom-phone-result="zoomPhoneResult"
            :audio-url="audioUrl"
            :audio-unavailable="audioUnavailable"
          />
        </div>
      </BLayout>
    </BHeightAdjuster>
  </div>
</template>

<script lang="ts" setup>
import { ref, computed, onBeforeMount, onMounted, onBeforeUnmount, DeepReadonly, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { GetZoomPhoneResult200ResponseZoomPhoneResultAllOfZoomPhoneRecording, Transcription as TTranscription, ZoomPhoneResult } from '@/api/openapi';
import { FileStorageAccessApiService } from '@/api/user/resources/file_storage_access';
import { ZoomPhoneResultApiService } from '@/api/user/resources/zoom_phone_result';
import SidePanel from '@/components/organisms/va-app/transcription/SidePanel.vue';
import TranscriptionHeader from '@/components/organisms/va-app/transcription/TranscriptionHeader.vue';
import TranscriptionMainContent from '@/components/organisms/va-app/transcription/TranscriptionMainContent.vue';
import { useAutoRetry } from '@/composable/use-auto-retry';
import { useTranscription } from '@/composable/user/transcription/transcription';
import { useZoomPhoneResultPrevNext } from '@/composable/va-app/use-zoom-phone-result-prev-next';
import { useWait } from '@/composable/vue-wait';

const route = useRoute();
const router = useRouter();
const zoomPhoneResultId = computed(() => Number(route.params.id));

const { doActionWithWait, wait } = useWait();
const {
  transcription,
  transcriptionExists,
  isTranscriptionResultEmpty,
  transcriptionInProgress,
  rightAfterTranscription,
  fetchTranscription: fetchTranscriptionFromComposable,
} = useTranscription();

const transcriptionMainContent = ref<InstanceType<typeof TranscriptionMainContent>>();
const zoomPhoneResult = ref<ZoomPhoneResult>();
const zoomPhoneRecording = ref<GetZoomPhoneResult200ResponseZoomPhoneResultAllOfZoomPhoneRecording>();
const callResultCommentLoading = ref<boolean>(false);
const summarizedResult = ref<string | undefined>();
const audioUrl = ref<string>();
const audioUnavailable = computed(() => {
  if (zoomPhoneRecording.value == null) return false;
  return zoomPhoneRecording.value.expired
    || zoomPhoneRecording.value.processingStatus === 'failed'
    || zoomPhoneRecording.value.processingStatus === 'not_found';
});
const loading = computed(() => {
  return wait.is(['fetchCallResultWait', 'deleteZoomPhoneResultWait']) || !!callResultCommentLoading.value;
});

const {
  prevId,
  nextId,
  prevPage,
  nextPage,
  refresh: refreshPrevNext,
} = useZoomPhoneResultPrevNext(
  () => zoomPhoneResultId.value || null,
  () => route.query as Record<string, string> | null);

const prevLink = computed(() => {
  if (prevId.value == null || prevPage.value == null) return null;
  return {
    name: 'VaTranscription',
    params: { id: prevId.value },
    query: { ...route.query, page: prevPage.value },
  };
});

const nextLink = computed(() => {
  if (nextId.value == null || nextPage.value == null) return null;
  return {
    name: 'VaTranscription',
    params: { id: nextId.value },
    query: { ...route.query, page: nextPage.value },
  };
});

const isEditableTarget = (target: EventTarget | null) => {
  if (!(target instanceof HTMLElement)) return false;
  return !!target.closest('input, textarea, select, [contenteditable="true"]');
};

const handleKeydown = (event: KeyboardEvent) => {
  if (event.defaultPrevented) return;
  if (event.altKey || event.ctrlKey || event.metaKey) return;
  if (isEditableTarget(event.target)) return;

  if (event.key === 'ArrowLeft' && prevLink.value) {
    event.preventDefault();
    void router.push(prevLink.value);
    return;
  }
  if (event.key === 'ArrowRight' && nextLink.value) {
    event.preventDefault();
    void router.push(nextLink.value);
  }
};

const fetchZoomPhoneResult = async () => {
  const api = new ZoomPhoneResultApiService();
  const isRetry = zoomPhoneResult.value != null;
  const waitKey = isRetry ? 'refetchZoomPhoneResultWait' : 'fetchZoomPhoneResultWait';
  await doActionWithWait(waitKey, async () => {
    const { data } = await api.getZoomPhoneResult({
      request: { id: zoomPhoneResultId.value },
    });
    if (isRetry) {
    // NOTE: retryはここだけ上書きする（それ以外のデータを無事に保つため）
      zoomPhoneRecording.value = data.zoomPhoneResult.zoomPhoneRecording;
    } else {
      zoomPhoneResult.value = data.zoomPhoneResult;
      zoomPhoneRecording.value = data.zoomPhoneResult.zoomPhoneRecording;
    }
  });
};
const fetchTranscription = async () => {
  if (!zoomPhoneResultId.value) return;
  await fetchTranscriptionFromComposable(zoomPhoneResultId.value);
};
const createAudioUrl = async () => {
  if (zoomPhoneRecording.value == null) return;
  if (audioUnavailable.value) return;
  if (zoomPhoneRecording.value.id == null) return;

  // データがまだない場合404をthrowするので止める
  try {
    await doActionWithWait('createAudioUrlWait', async () => {
      const apiService = new FileStorageAccessApiService();
      const { data } = await apiService.createZoomPhoneRecordingPresignedUrl({
        request: {
          createZoomPhoneRecordingPresignedUrlRequest: {
            zoomPhoneRecordingId: zoomPhoneRecording.value.id,
          },
        },
        errorHandlers: {
          404: () => {
            audioUrl.value = undefined;
          },
        },
      });
      audioUrl.value = data.url;
    });
  } catch (error) {
    if (error.response.status !== 404) {
      throw error;
    }
  }
};

const handleUpdateSummarizedResult = (result: string | undefined) => {
  summarizedResult.value = result;
};

const playAudio = (time: number) => {
  transcriptionMainContent.value?.playAudio(time);
};

const checkCallResultAvailability = async (): Promise<boolean> => {
  await fetchZoomPhoneResult();
  // NOTE: ZoomPhoneResultのデータがまだない場合があるのでその時はリトライ
  return zoomPhoneRecording.value?.processingStatus !== 'processing' && zoomPhoneRecording.value?.processingStatus !== 'not_started';
};
const { start: startCallResultRetry } = useAutoRetry(
  checkCallResultAvailability,
  {
    interval: 5000,
    maxRetries: 3,
  },
);

const isAudioFetchFinished = (audioUrl: string | undefined) => audioUrl != null || audioUnavailable.value;
const checkAudioAvailability = async (): Promise<boolean> => {
  if (isAudioFetchFinished(audioUrl.value)) return true;

  await createAudioUrl();
  return isAudioFetchFinished(audioUrl.value);
};
const { start: startAudioRetry } = useAutoRetry(
  checkAudioAvailability,
  {
    interval: 5000,
    maxRetries: 3, // audio URLは短時間で決着がつくはず
  },
);

// Transcription取得の自動再取得
const isTranscriptionFetchFinished = (trs: DeepReadonly<TTranscription>) => trs?.id != null || trs?.processingStatus === 'failed';
const checkTranscriptionAvailability = async (): Promise<boolean> => {
  if (isTranscriptionFetchFinished(transcription.value)) return true;

  await fetchTranscription();
  return isTranscriptionFetchFinished(transcription.value);
};
const { start: startTranscriptionRetry } = useAutoRetry(
  checkTranscriptionAvailability,
  {
    interval: 15000,
    maxRetries: 20, // transcriptionは時間がかかる場合がある
  },
);

onBeforeMount(async () => {
  await startCallResultRetry();
  await startAudioRetry();
  await startTranscriptionRetry();
  await refreshPrevNext();
});

onMounted(() => {
  window.addEventListener('keydown', handleKeydown);
});

onBeforeUnmount(() => {
  window.removeEventListener('keydown', handleKeydown);
});

watch(() => zoomPhoneResultId.value, async (newId, oldId) => {
  if (newId === oldId) return;
  zoomPhoneResult.value = undefined;
  zoomPhoneRecording.value = undefined;
  audioUrl.value = undefined;
  summarizedResult.value = undefined;
  await startCallResultRetry();
  await startAudioRetry();
  await startTranscriptionRetry();
  await refreshPrevNext();
});
</script>

<style lang="scss" scoped>
.container {
  @include m-fixed-height(100vh);
  display: flex;
  position: relative;
  max-width: 100%;
  width: 100%;
  overflow: hidden;

  .adjuster {
    width: 100%;
    overflow: hidden;

    .header {
      @include m-fixed-height(50px);
      margin-top: 50px;
      border-bottom: 1px solid $bdcolor-light;
      flex: 0 0 auto;
    }

    .body-left {
      border-right: 1px solid $bdcolor-light;
      @include m-fixed-width(400px);
    }

    .body-right {
      padding: $basespace-500;
      overflow: hidden;
      display: flex;
      flex-direction: column;
      @include m-fixed-width(calc(100% - 400px));
    }
  }
}
</style>
